import { Archive, ArchiveRestore, FileText, Info, Paperclip, X } from 'lucide-react';
import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ConversationDto } from '@/Domain/api/Conversation/dto/Conversation.dto';
import { Message } from '@/Domain/api/Conversation/dto/Message.dto';
import { AssignUser } from '@/Domain/components/Inbox/ConversationDetails/Tabs/DetailsTab/AssignUser';
import { AudioPlayer } from '@/Domain/components/Inbox/Message/AudioPlayer';
import {
  AudioRecorderButton,
  formatDuration,
  useAudioRecorder,
} from '@/Domain/components/Inbox/MessageInput/AudioRecorder';
import { WhatsAppTemplateDropdown } from '@/Domain/components/Inbox/MessageInput/WhatsAppTemplateDropdown';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { PaperPlaneIcon } from '@/Domain/components/icons/PaperPlane.icon';
import { ConversationAssigneeEnum } from '@/Domain/enum/ConversationAssignee.enum';
import { ConversationSourceEnum } from '@/Domain/enum/ConversationSource.enum';
import { ConversationStatusEnum } from '@/Domain/enum/ConversationStatus.enum';
import { useChangeConversationStatus } from '@/Domain/hooks/Conversation/useChangeConversationStatus.hook';
import { useConversationSendMedia } from '@/Domain/hooks/Conversation/useConversationSendMedia.hook';
import { useConversationReply } from '@/Domain/hooks/Conversation/useConvrsationReply.hook';
import { useUpdateConversation } from '@/Domain/hooks/Conversation/useUpdateConversation.hook';
import { useUser } from '@/Domain/hooks/User/useUser.hook';

const MESSAGING_WINDOW_MS = 24 * 60 * 60 * 1000;

// Channels where a business may only send free-form messages inside 24h of the
// person's last message: WhatsApp's customer service window, and Instagram's
// equivalent (InstagramSendErrorCategory.OUTSIDE_24H_WINDOW).
const MESSAGING_WINDOW_SOURCES = [
  ConversationSourceEnum.WHATSAPP,
  ConversationSourceEnum.INSTAGRAM,
];

const WHATSAPP_MESSAGING_WINDOW_DOC_URL =
  'https://developers.facebook.com/documentation/business-messaging/whatsapp/messages/send-messages#customer-service-windows';
const INSTAGRAM_MESSAGING_WINDOW_DOC_URL =
  'https://developers.facebook.com/docs/instagram-platform/instagram-api-with-instagram-login/messaging-api/';

// Sources the Inbox composer can send media to (the UI omits Telegram).
const MEDIA_CAPABLE_SOURCES = [
  ConversationSourceEnum.WHATSAPP,
  ConversationSourceEnum.INSTAGRAM,
  ConversationSourceEnum.MESSENGER,
  ConversationSourceEnum.WIDGET,
  ConversationSourceEnum.EMBED,
  ConversationSourceEnum.PUBLIC__LINK,
  ConversationSourceEnum.PREVIEW,
];

// Meta's Send API fetches an attachment by URL and carries text or an attachment, never both.
const META_SEND_API_SOURCES = [ConversationSourceEnum.INSTAGRAM, ConversationSourceEnum.MESSENGER];

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ALLOWED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
];
const ALLOWED_AUDIO_TYPES = [
  'audio/ogg',
  'audio/mpeg',
  'audio/mp4',
  'audio/aac',
  'audio/amr',
  'audio/webm',
];
const IMAGE_AND_DOCUMENT_TYPES = [...ALLOWED_IMAGE_TYPES, ...ALLOWED_DOCUMENT_TYPES];
const ALL_ALLOWED_TYPES = [...IMAGE_AND_DOCUMENT_TYPES, ...ALLOWED_AUDIO_TYPES];
const ACCEPT_STRING_NO_AUDIO = '.jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt';
const ACCEPT_STRING = `${ACCEPT_STRING_NO_AUDIO},.ogg,.mp3,.mp4,.m4a,.webm`;
// Refuse unsupported Meta attachments before the operator uploads them.
const META_SEND_API_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
const ACCEPT_STRING_META_SEND_API = '.jpg,.jpeg,.png,.pdf';
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 MB (WhatsApp Cloud API limit)
const MAX_DOCUMENT_SIZE = 16 * 1024 * 1024; // 16 MB
const MAX_AUDIO_SIZE = 16 * 1024 * 1024; // 16 MB

type PendingAttachment = {
  file: File;
  preview: string | null;
  filename: string;
  type: 'image' | 'document' | 'audio';
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type Props = {
  conversation: ConversationDto;
};

export const MessageInput = ({ conversation }: Props) => {
  const { t } = useTranslation(['inbox_v2']);
  const { handleReply } = useConversationReply(conversation);
  const { handleSendMedia, isPending: isSendMediaPending } = useConversationSendMedia(conversation);
  const { handleUpdate } = useUpdateConversation(conversation.id);
  const { user } = useUser();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingAttachment, setPendingAttachment] = useState<PendingAttachment | null>(null);
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const [assignUserDropdownOpen, setAssignUserDropdownOpen] = useState(false);
  const usesMetaSendApi = META_SEND_API_SOURCES.includes(conversation.source);
  const sourceSupportsMedia =
    MEDIA_CAPABLE_SOURCES.includes(conversation.source) &&
    (conversation.supports_inbox_media ?? !usesMetaSendApi);

  // The deadline, not the verdict: a conversation can sit open across it with no
  // socket event to re-render on, so the verdict has to be derived from a clock the
  // component actually advances. `null` means no window applies to this conversation.
  const messagingWindowDeadline = useMemo(() => {
    if (
      !MESSAGING_WINDOW_SOURCES.includes(conversation.source) ||
      conversation.assignee.type !== ConversationAssigneeEnum.HUMAN_OPERATOR ||
      user?.id !== conversation.assignee.user.id
    ) {
      return null;
    }
    if (!conversation.last_visitor_message_at) return 0;
    return new Date(conversation.last_visitor_message_at).getTime() + MESSAGING_WINDOW_MS;
  }, [conversation, user?.id]);

  const [clockMs, setClockMs] = useState(() => Date.now());
  useEffect(() => {
    if (messagingWindowDeadline === null) return;
    const remaining = messagingWindowDeadline - Date.now();
    if (remaining <= 0) return;
    // Always under 24h, so comfortably inside setTimeout's 2^31-1 ms ceiling.
    const timer = window.setTimeout(() => setClockMs(Date.now()), remaining);
    return () => window.clearTimeout(timer);
  }, [messagingWindowDeadline]);

  const isOutsideMessagingWindow =
    messagingWindowDeadline !== null && messagingWindowDeadline <= clockMs;

  const canSendMedia = useMemo(
    () =>
      sourceSupportsMedia &&
      conversation.assignee.type === ConversationAssigneeEnum.HUMAN_OPERATOR &&
      user?.id === conversation.assignee.user?.id &&
      // Always false for a source with no messaging window.
      !isOutsideMessagingWindow &&
      conversation.resolution_status !== ConversationStatusEnum.Resolved,
    [conversation, user?.id, isOutsideMessagingWindow, sourceSupportsMedia],
  );

  // Audio is WhatsApp-only; the widget surfaces support image + document, so no voice recorder / audio files there.
  const audioAllowed = conversation.source === ConversationSourceEnum.WHATSAPP;
  const allowedTypes = usesMetaSendApi
    ? META_SEND_API_ALLOWED_TYPES
    : audioAllowed
      ? ALL_ALLOWED_TYPES
      : IMAGE_AND_DOCUMENT_TYPES;
  const acceptString = usesMetaSendApi
    ? ACCEPT_STRING_META_SEND_API
    : audioAllowed
      ? ACCEPT_STRING
      : ACCEPT_STRING_NO_AUDIO;

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<Pick<Message, 'content'>>({
    defaultValues: { content: '' },
  });

  const {
    handleChangeStatus,
    canChangeStatus,
    isPending: changeStatusIsPending,
  } = useChangeConversationStatus(conversation);

  useEffect(() => {
    const preview = pendingAttachment?.preview;
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [pendingAttachment?.preview]);

  const clearAttachment = useCallback(() => {
    setPendingAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleFileSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!allowedTypes.includes(file.type)) {
        toast.error(t('conversation.attachment_invalid_type'));
        e.target.value = '';
        return;
      }

      const isImage = ALLOWED_IMAGE_TYPES.includes(file.type);
      const isAudio = ALLOWED_AUDIO_TYPES.includes(file.type);
      const maxSize = isImage ? MAX_IMAGE_SIZE : isAudio ? MAX_AUDIO_SIZE : MAX_DOCUMENT_SIZE;
      if (file.size > maxSize) {
        const maxMb = maxSize / (1024 * 1024);
        toast.error(t('conversation.attachment_too_large', { size: `${maxMb}MB` }));
        e.target.value = '';
        return;
      }

      const preview = isImage || isAudio ? URL.createObjectURL(file) : null;
      setPendingAttachment({
        file,
        preview,
        filename: file.name,
        type: isImage ? 'image' : isAudio ? 'audio' : 'document',
      });
    },
    [t, allowedTypes],
  );

  const handleAudioRecorded = useCallback((file: File) => {
    const preview = URL.createObjectURL(file);
    setPendingAttachment({ file, preview, filename: file.name, type: 'audio' });
  }, []);

  const { isRecording, duration, startRecording, stopRecording } = useAudioRecorder({
    onRecordingComplete: handleAudioRecorded,
  });

  const onSubmit = handleSubmit((data): void => {
    if (messagingWindowDeadline !== null && messagingWindowDeadline <= Date.now()) {
      setClockMs(Date.now());
      return;
    }
    // Enter must also wait for the attachment so retained text cannot overtake it.
    if (isSendMediaPending) return;
    if (pendingAttachment) {
      // Meta attachments carry no caption, so retain the text for the next send.
      handleSendMedia(
        pendingAttachment.file,
        usesMetaSendApi ? undefined : data.content || undefined,
      );
      clearAttachment();
      if (!usesMetaSendApi) reset();
    } else {
      reset();
      handleReply(data);
    }
  });

  const onKeyDownForm = (e: KeyboardEvent<HTMLFormElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  // Clearing the attachment must not let retained text overtake its in-flight send.
  const canSubmit = !isSendMediaPending && (pendingAttachment !== null || isValid);

  return (
    <>
      <form
        onSubmit={onSubmit}
        className="bg-card rounded-xl mb-2 py-3 pl-4 pr-3 flex flex-col justify-between gap-3 border border-border shadow-lg focus-within:!ring-[3px] focus-within:ring-ring/25 focus-within:!border-foreground focus-within:!mt-[3px]"
        onKeyDown={onKeyDownForm}
      >
        {pendingAttachment && pendingAttachment.type === 'audio' && pendingAttachment.preview && (
          <div className="flex items-center gap-2" data-testid="attachment-preview">
            <AudioPlayer
              src={pendingAttachment.preview}
              mimeType={pendingAttachment.file.type}
              className="flex-1"
            />
            <button
              type="button"
              onClick={clearAttachment}
              className="text-foreground-subtle hover:text-muted-foreground p-1 shrink-0"
              data-testid="attachment-remove"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        {pendingAttachment && pendingAttachment.type !== 'audio' && (
          <div
            className="flex items-center gap-3 rounded-lg bg-muted border border-border p-2"
            data-testid="attachment-preview"
          >
            {pendingAttachment.preview ? (
              <img
                src={pendingAttachment.preview}
                alt={pendingAttachment.filename}
                className="h-12 w-12 rounded object-cover"
              />
            ) : (
              <div className="h-12 w-12 rounded bg-muted flex items-center justify-center">
                <FileText className="h-6 w-6 text-foreground-subtle" />
              </div>
            )}
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-sm text-muted-foreground truncate">
                {pendingAttachment.filename}
              </span>
              <span className="text-xs text-foreground-subtle">
                {formatFileSize(pendingAttachment.file.size)}
              </span>
            </div>
            <button
              type="button"
              onClick={clearAttachment}
              className="text-foreground-subtle hover:text-muted-foreground p-1"
              data-testid="attachment-remove"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        {isRecording ? (
          <div
            className="flex items-center justify-center gap-2 h-[24px]"
            data-testid="audio-recorder-recording"
          >
            <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-sm text-foreground-subtle">
              {t('conversation.audio_recording')}
            </span>
            <span className="text-sm text-foreground-subtle tabular-nums">
              {formatDuration(duration)}
            </span>
          </div>
        ) : (
          <Controller
            control={control}
            name="content"
            rules={{ required: !pendingAttachment }}
            render={({ field }) => (
              <AutosizeTextarea
                autoComplete="off"
                placeholder={
                  conversation?.assignee.type === ConversationAssigneeEnum.UNASSIGNED ||
                  (conversation?.assignee.type === ConversationAssigneeEnum.HUMAN_OPERATOR &&
                    user?.id !== conversation?.assignee.user.id)
                    ? t('conversation.input_placeholder_unassigned')
                    : conversation?.assignee.type !== ConversationAssigneeEnum.HUMAN_OPERATOR
                      ? t('conversation.input_placeholder_ai_managed')
                      : conversation.resolution_status === ConversationStatusEnum.Resolved
                        ? t('conversation.input_placeholder_resolved')
                        : isOutsideMessagingWindow
                          ? conversation.source === ConversationSourceEnum.INSTAGRAM
                            ? t('conversation.input_placeholder_instagram_24h')
                            : t('conversation.input_placeholder_whatsapp_24h')
                          : t('conversation.input_placeholder')
                }
                className="rounded-none !border-none focus:!ring-0 py-0 px-0 bg-transparent"
                maxHeight={120}
                disabled={
                  conversation.resolution_status === ConversationStatusEnum.Resolved ||
                  conversation?.assignee.type !== ConversationAssigneeEnum.HUMAN_OPERATOR ||
                  (conversation?.assignee.type === ConversationAssigneeEnum.HUMAN_OPERATOR &&
                    user?.id !== conversation?.assignee.user.id) ||
                  isOutsideMessagingWindow
                }
                {...field}
              />
            )}
          />
        )}
        {isOutsideMessagingWindow && (
          <div
            className="flex items-start gap-2 px-1 py-1.5 text-xs text-foreground-subtle"
            data-testid="messaging-window-notice"
          >
            <Info className="shrink-0 mt-0.5 size-4" />
            {conversation.source === ConversationSourceEnum.INSTAGRAM ? (
              <Trans
                i18nKey="conversation.instagram_24h_info"
                t={t}
                parent="span"
                components={{
                  windowLink: (
                    <a
                      href={INSTAGRAM_MESSAGING_WINDOW_DOC_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 font-medium hover:text-blue-800"
                    />
                  ),
                }}
              />
            ) : (
              <Trans
                i18nKey="conversation.whatsapp_24h_info"
                t={t}
                parent="span"
                components={{
                  windowLink: (
                    <a
                      href={WHATSAPP_MESSAGING_WINDOW_DOC_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 font-medium hover:text-blue-800"
                    />
                  ),
                  templateLink: (
                    <button
                      type="button"
                      onClick={() => setTemplateDropdownOpen(true)}
                      className="text-blue-600 font-medium hover:text-blue-800"
                    />
                  ),
                }}
              />
            )}
          </div>
        )}
        <TooltipProvider>
          <div className="w-full flex items-end justify-between">
            <div className="flex gap-2 min-w-0">
              {canSendMedia && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <IconButton
                        variant="primary_dark_light"
                        size="sm"
                        type="button"
                        disabled={isSendMediaPending}
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="attachment-button"
                        aria-label={t(
                          isSendMediaPending
                            ? 'conversation.attachment_button_sending'
                            : 'conversation.attachment_button_tooltip',
                        )}
                      >
                        {isSendMediaPending ? (
                          <Spinner className="h-4 w-4" />
                        ) : (
                          <Paperclip className="h-4 w-4" />
                        )}
                      </IconButton>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {t('conversation.attachment_button_tooltip')}
                    </TooltipContent>
                  </Tooltip>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={acceptString}
                    className="hidden"
                    onChange={handleFileSelect}
                    data-testid="attachment-file-input"
                  />
                  {audioAllowed && (
                    <AudioRecorderButton
                      disabled={isSendMediaPending || !!pendingAttachment}
                      isRecording={isRecording}
                      onStart={startRecording}
                      onStop={stopRecording}
                    />
                  )}
                </>
              )}
              {conversation?.assignee.type === ConversationAssigneeEnum.HUMAN_OPERATOR &&
                user?.id === conversation?.assignee.user?.id &&
                conversation?.source === ConversationSourceEnum.WHATSAPP && (
                  <WhatsAppTemplateDropdown
                    conversationId={conversation.id}
                    open={templateDropdownOpen}
                    onOpenChange={setTemplateDropdownOpen}
                  />
                )}
            </div>
            <div className="flex gap-2 min-w-0">
              {conversation?.assignee.type === ConversationAssigneeEnum.HUMAN_OPERATOR &&
                user?.id === conversation?.assignee.user?.id && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="primary_dark_light"
                        disabled={!canChangeStatus || changeStatusIsPending}
                        className="disabled:!cursor-not-allowed min-w-0 overflow-hidden"
                        onClick={() =>
                          conversation.resolution_status === ConversationStatusEnum.Open
                            ? handleChangeStatus(ConversationStatusEnum.Resolved)
                            : handleChangeStatus(ConversationStatusEnum.Open)
                        }
                      >
                        {conversation.resolution_status === ConversationStatusEnum.Open ? (
                          <>
                            <Archive className="size-4" />
                            <span className="truncate">{t('button.close')}</span>
                          </>
                        ) : (
                          <>
                            <ArchiveRestore className="size-4" />
                            <span className="truncate">{t('button.reopen')}</span>
                          </>
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {conversation.resolution_status === ConversationStatusEnum.Open
                        ? t('button.close_tooltip')
                        : t('button.reopen_tooltip')}
                    </TooltipContent>
                  </Tooltip>
                )}
              {!(
                conversation?.assignee.type === ConversationAssigneeEnum.HUMAN_OPERATOR &&
                user?.id === conversation?.assignee.user.id
              ) && (
                <Button
                  size="sm"
                  variant="primary_dark_light"
                  onClick={() =>
                    handleUpdate({
                      assignee: {
                        type: ConversationAssigneeEnum.HUMAN_OPERATOR,
                        user: { id: user?.id, email: user?.email },
                      },
                    })
                  }
                >
                  {t('button.assign_to_me')}
                </Button>
              )}
              {conversation?.supports_inbox_control && (
                <Tooltip open={assignUserDropdownOpen ? false : undefined}>
                  <TooltipTrigger asChild>
                    <div className="flex justify-end">
                      <AssignUser
                        assignee={conversation.assignee}
                        conversationId={conversation.id}
                        location="action-bar"
                        onOpenChange={setAssignUserDropdownOpen}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {t('button.assign_conversation_tooltip')}
                  </TooltipContent>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconButton
                    variant="dark"
                    size="sm"
                    disabled={!canSubmit || isOutsideMessagingWindow}
                    className="disabled:!cursor-not-allowed"
                    onClick={onSubmit}
                    data-testid="send-message-button"
                  >
                    <PaperPlaneIcon className="hover:cursor-pointer" />
                  </IconButton>
                </TooltipTrigger>
                <TooltipContent side="top">{t('button.send_message_tooltip')}</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </TooltipProvider>
      </form>
    </>
  );
};
